import type { ReactNode } from "react";
import { Icon } from "@majourneys/icons";
import { BEVEL_INSET_NAVY, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

/**
 * The furniture a tool page needs to be a page.
 *
 * A live surface — a chat, a planner — has nothing in it until someone uses
 * it, so on its own it is a blank panel to a crawler and to a first-time
 * visitor alike. Mindtrip answers this by keeping their chat out of the index
 * entirely (`Disallow: /chat` in their robots.txt) and ranking a landing page
 * about it instead. These are the parts of that landing page, shared by
 * `/ai-chat` and `/trip-planner` so the two cannot drift into different
 * shapes.
 *
 * Everything here is server-rendered from locale strings. The tool itself
 * hydrates in the middle of it.
 */

const NAVY = "#110E39";
const MUTED = "#5a5d77";
const GOLD_INK = "#8A6425";
const HAIRLINE = "rgba(17,14,57,0.09)";

export function LandingHero({
  eyebrow,
  title,
  intro,
  cta,
}: {
  eyebrow: string;
  title: string;
  intro: string;
  /**
   * The way through to the live surface. A landing page that only embeds the
   * tool leaves the guest with no obvious next move — and the embedded panel
   * is the smaller version, without the map beside it.
   */
  cta?: { href: string; label: string };
}) {
  return (
    <header className="mx-auto max-w-[760px] px-4 pt-10 text-center sm:pt-14">
      <p
        className="text-[11px] font-bold uppercase"
        style={{ color: GOLD_INK, letterSpacing: "0.12em" }}
      >
        {eyebrow}
      </p>
      {/* The page's only h1. The tool below it carries h2s. */}
      <h1
        className="mt-2 text-[30px] font-semibold sm:text-[38px]"
        style={{ color: NAVY, letterSpacing: "-0.03em", lineHeight: 1.1 }}
      >
        {title}
      </h1>
      <p className="mx-auto mt-3 max-w-[620px] text-[15px] leading-[24px]" style={{ color: MUTED }}>
        {intro}
      </p>
      {cta ? (
        <a
          href={cta.href}
          className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14.5px] font-semibold text-white transition-opacity hover:opacity-95"
          style={{
            background: BEVEL_NAVY_FACE,
            boxShadow: `0 10px 24px -12px rgba(17,14,57,0.8), ${BEVEL_INSET_NAVY}`,
          }}
        >
          {cta.label}
          <Icon name="ri-arrow-right-line" size={16} className="rtl:-scale-x-100" aria-hidden="true" />
        </a>
      ) : null}
    </header>
  );
}

export interface LandingStep {
  icon: string;
  title: string;
  body: string;
}

export function LandingSteps({ title, steps }: { title: string; steps: readonly LandingStep[] }) {
  return (
    <section className="mx-auto mt-10 max-w-[1000px] px-4">
      <h2 className="text-center text-[20px] font-semibold" style={{ color: NAVY, letterSpacing: "-0.02em" }}>
        {title}
      </h2>
      <ol className="mt-5 grid gap-3 sm:grid-cols-3">
        {steps.map((s, i) => (
          <li
            key={s.title}
            className="rounded-3xl bg-white p-5"
            style={{ boxShadow: `0 0 0 1px ${HAIRLINE}, 0 8px 30px rgba(17,14,57,0.05)` }}
          >
            <div className="flex items-center gap-2">
              <span
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[13px] font-bold text-white"
                style={{ background: "linear-gradient(135deg, #2E2878 0%, #1A1454 48%, #110E39 100%)" }}
              >
                {i + 1}
              </span>
              <Icon name={s.icon} size={18} style={{ color: GOLD_INK }} aria-hidden="true" />
            </div>
            <h3 className="mt-3 text-[15px] font-bold" style={{ color: NAVY }}>
              {s.title}
            </h3>
            <p className="mt-1.5 text-[13.5px] leading-[20px]" style={{ color: MUTED }}>
              {s.body}
            </p>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** The tool, framed and labelled so it is not a mystery panel. */
export function LandingTool({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mx-auto mt-12 w-full max-w-[1180px] px-4">
      <h2 className="sr-only">{title}</h2>
      {children}
    </section>
  );
}

/** Internal links, which is half of why this page exists. */
export function LandingLinks({
  title,
  links,
}: {
  title: string;
  links: readonly { href: string; label: string }[];
}) {
  if (links.length === 0) return null;
  return (
    <section className="mx-auto mt-12 max-w-[1000px] px-4">
      <h2 className="text-[20px] font-semibold" style={{ color: NAVY, letterSpacing: "-0.02em" }}>
        {title}
      </h2>
      <ul className="mt-4 flex flex-wrap gap-2">
        {links.map((l) => (
          <li key={l.href}>
            <a
              href={l.href}
              className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13.5px] font-semibold transition-shadow hover:shadow-[0_0_0_1.5px_rgba(17,14,57,0.34)]"
              style={{
                color: NAVY,
                background: BEVEL_WHITE_FACE,
                boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1.5px rgba(17,14,57,0.16)`,
              }}
            >
              {l.label}
              <Icon name="ri-arrow-right-line" size={14} className="rtl:-scale-x-100" aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface LandingFaq {
  question: string;
  answer: string;
}

/**
 * Rendered as real text, not an accordion that hides it behind JavaScript —
 * and paired with FAQPage JSON-LD by the page that owns it. Each answer has
 * to stand alone as a citable snippet, per the explore-item content policy.
 */
export function LandingFaqs({ title, faqs }: { title: string; faqs: readonly LandingFaq[] }) {
  return (
    <section className="mx-auto mt-12 max-w-[800px] px-4 pb-16">
      <h2 className="text-[20px] font-semibold" style={{ color: NAVY, letterSpacing: "-0.02em" }}>
        {title}
      </h2>
      <dl className="mt-4">
        {faqs.map((f, i) => (
          <div
            key={f.question}
            className={i > 0 ? "border-t pt-4" : ""}
            style={i > 0 ? { borderColor: HAIRLINE, marginTop: 16 } : undefined}
          >
            <dt className="text-[15px] font-bold" style={{ color: NAVY }}>
              {f.question}
            </dt>
            <dd className="mt-1.5 text-[14px] leading-[22px]" style={{ color: MUTED }}>
              {f.answer}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** The schema that turns the block above into a rich result. */
export function faqPageLd(faqs: readonly LandingFaq[], id: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": id,
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
}
